import algolia from '@/algolia'
import axios, { Canceler } from 'axios'
import axiosInstance from '../../axios'

// initial state
const state = {
  allResults: <any[]>[],
  articles: <any[]>[],
  accounts: <any[]>[],
  subscriptions: <any[]>[],
  users: <any[]>[],
  companies: <any[]>[],
  analysts: <any[]>[],
  channels: <any[]>[],
  query: <string>'',
  filter: <string>'',
  results: <any[]>[],
}

const getters = {}

const CancelToken = axios.CancelToken
let cancel: Canceler

const actions = {
  search({ commit }: any, query: string) {
    if (cancel) {
      cancel('aborted')
    }

    const queries = [
      {
        indexName: 'accounts',
        query,
        params: {
          hitsPerPage: 5,
        },
      },
      {
        indexName: 'subscriptions',
        query,
        params: {
          hitsPerPage: 5,
        },
      },
      {
        indexName: 'users',
        query,
        params: {
          hitsPerPage: 5,
        },
      },
      {
        indexName: 'companies',
        query,
        params: {
          hitsPerPage: 5,
        },
      },
      {
        indexName: 'analysts',
        query,
        params: {
          hitsPerPage: 5,
        },
      },
      {
        indexName: 'channels',
        query,
        params: {
          hitsPerPage: 5,
        },
      },
    ]

    const data = JSON.parse(
      `{"variables":{"first":10,"search":${JSON.stringify(query)},"semanticSearch":true},"query":"query ($after: String, $first: Int, $groups: [[ListedArticleGroup!]!], $channelSlug: String, $companySlug: String, $regionSlug: String, $focused: Boolean, $bookmarked: Boolean, $articleIds: [Int!], $semanticSearch: Boolean!, $search: String) {\\n  articles(\\n    first: $first\\n    after: $after\\n    groups: $groups\\n    channelSlug: $channelSlug\\n    companySlug: $companySlug\\n    regionSlug: $regionSlug\\n    bookmarked: $bookmarked\\n    focused: $focused\\n    articleIds: $articleIds\\n    search: $search\\n  ) {\\n    pageInfo {\\n      endCursor\\n      hasNextPage\\n      totalCount\\n      __typename\\n    }\\n    edges {\\n      node {\\n        ...ListedArticleFragment @skip(if: $semanticSearch)\\n        ...SemanticSearchFragment @include(if: $semanticSearch)\\n        __typename\\n      }\\n      __typename\\n    }\\n    __typename\\n  }\\n}\\n\\nfragment ListedArticleFragment on ListedArticle {\\n  lastViewedAt\\n  isFocused\\n  title\\n  slug\\n  summary\\n  publishedAt\\n  externalUrl\\n  isProExclusive\\n  regionIds\\n  responsiveImage {\\n    srcsets {\\n      value\\n      media\\n      type\\n      __typename\\n    }\\n    src\\n    alt\\n    __typename\\n  }\\n  place {\\n    name\\n    slug\\n    __typename\\n  }\\n  contentType {\\n    name\\n    color\\n    __typename\\n  }\\n  channel {\\n    id\\n    isPremier\\n    name\\n    badge\\n    __typename\\n  }\\n  analysts {\\n    name\\n    hasLeftChannel\\n    responsivePicture {\\n      round {\\n        srcsets {\\n          value\\n          media\\n          type\\n          __typename\\n        }\\n        src\\n        alt\\n        __typename\\n      }\\n      square {\\n        srcsets {\\n          value\\n          media\\n          type\\n          __typename\\n        }\\n        src\\n        alt\\n        __typename\\n      }\\n      __typename\\n    }\\n    picture {\\n      round\\n      square\\n      __typename\\n    }\\n    channel {\\n      name\\n      badge\\n      __typename\\n    }\\n    __typename\\n  }\\n  __typename\\n}\\n\\nfragment SemanticSearchFragment on ListedArticle {\\n  title\\n  slug\\n  summary\\n  searchText\\n  publishedAt\\n  isProExclusive\\n  regionIds\\n  contentType {\\n    id\\n    name\\n    color\\n    __typename\\n  }\\n  channel {\\n    id\\n    isPremier\\n    name\\n    badge\\n    __typename\\n  }\\n id \\n  analysts {\\n    name\\n    __typename\\n  }\\n  __typename\\n}"}`
    )
    const response = axiosInstance.post('https://tellimer.com/graphql', data, {
      cancelToken: new CancelToken((c) => (cancel = c)),
    })
    response.then((r) => commit('setArticles', r)).catch(e => {
      console.log(e)
    })

    algolia.search(queries, (err, results) => {
      commit('setResults', results)
      commit('setQuery', query)
      commit('filterBy', 'all')
    })
  },
}

// mutations
const mutations = {
  setArticles(state: any, content: any) {
    state.articles = content.data.data.articles.edges.map((v) => {
      v.node.link = { name: 'articles.view', params: { articleId: v.node.id } }
      v.node.type = 'Articles'
      v.node.name = v.node.title
      return v.node
    })

    state.allResults = [
      ...state.articles,
      ...state.accounts,
      ...state.subscriptions,
      // ...users,
      ...state.companies,
      ...state.analysts,
      ...state.channels,
    ]
  },
  setResults(state: any, content: algoliasearch.MultiResponse) {
    state.accounts = content.results[0].hits.map((v) => {
      v.link = { name: 'accounts.view', params: { accountId: v.id } }
      v.type = 'Accounts'
      return v
    })
    state.subscriptions = content.results[1].hits.map((v) => {
      v.link = { name: 'subscriptions.view', params: { subscriptionId: v.id } }
      v.type = 'Subscriptions'
      return v
    })
    state.users = content.results[2].hits.map((v) => {
      v.link = { name: 'users.view', params: { userId: v.id } }
      v.type = 'Users'
      return v
    })
    state.companies = content.results[3].hits.map((v) => {
      v.link = { name: 'companies.view', params: { companyId: v.id } }
      v.type = 'Companies'
      return v
    })
    state.analysts = content.results[4].hits.map((v) => {
      v.link = { name: 'analysts.view', params: { analystId: v.id } }
      v.type = 'Analysts'
      return v
    })
    state.channels = content.results[5].hits.map((v) => {
      v.link = { name: 'channels.edit', params: { articleId: v.objectID } }
      v.type = 'Channels'
      return v
    })

    state.allResults = [
      ...state.accounts,
      ...state.subscriptions,
      // ...users,
      ...state.companies,
      ...state.analysts,
      ...state.channels,
    ]
  },

  setQuery(state: any, query: string) {
    state.query = query
  },

  filterBy(state: any, filter: string) {
    state.filterBy = filter.toLowerCase()
    state.results = state[state.filterBy] || state.allResults
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
